// How many pieces are installed, and which ones — the site inventory.
//
// ── This is NOT the User Access report, and the difference is the point ────
//
// core/admin/accessReport answers "what can this USER reach", and it reads the
// mapping tables to do it. That is the wrong question for an installation
// count, because a device is installed whether or not anybody has been given
// access to it. Measured on location 18: 37 pieces are installed, and the
// User Access report for the user who owns the site shows 13 — he is allotted
// one of its three bedrooms. Handing that sheet over as an installation count
// under-reports by 65%, and nothing on it says so.
//
// So this reads `GetAllDevicesByLocationID`, which takes NO UserId and answers
// for the premises:
//
//     UniqueNumber, RoomName, DeviceName, DeviceTypeName,
//     NetworkLastUpdatedDateTime, Connected
//
// — the four columns a handover sheet needs, plus the two that say whether the
// piece is alive, in one request per site.
//
// ⚠️ `RoomName` IS NULL FOR A DEVICE THAT BELONGS TO THE LOCATION, not to a
// room — the gates, meters and pumps. It is named "Common" rather than left
// blank: an empty cell on an inventory reads as missing data, and somebody goes
// looking for the room that was never there.

import { getDevicesByLocation } from '../api/api';
import { collectEstateDevices, liveDotOf } from '../devices/firmwareFleet';

export const COMMON_ROOM = 'Common';

// ── Columns ────────────────────────────────────────────────────────────────
//
// The four asked for, plus Sr. No. and the live mark. `Location Name` only
// appears on the estate-wide sheet — on a single site it would be the same
// value on every row, which is a column that costs width and says nothing.

/*
 * ⚠️ WIDTHS ARE PERCENTAGES, AND EACH SET SUMS TO 100. They used to be
 * millimetres, which is a PAPER unit: the on-screen table had no widths at all
 * and sized its columns by content, so the preview and the printed sheet showed
 * the same data in different proportions. A percentage is of whatever it is in —
 * 190mm of A4, or the width of a dialog — so one set of numbers lays out both.
 * See the colgroup note in ui/shared/printReport.
 */
const SR = { key: 'sr', label: 'Sr. No.', width: '8%', align: 'right' };

// The live tally, beside the count. ⚠️ pieces is NOT always online + offline —
// a device with no heartbeat column is neither, and that difference is shown in
// the header rather than guessed at. See installedRow.
const ONLINE = { key: 'online', label: 'Online', width: '17%', align: 'right', tone: (v) => (v ? 'ok' : '') };
const OFFLINE = { key: 'offline', label: 'Offline', width: '16%', align: 'right', tone: (v) => (v ? 'bad' : '') };

/*
 * `tone` colours the cell — green/red/nothing — and the vocabulary lives HERE
 * rather than in the printer, which would otherwise have to know every report's
 * words. The words alone still carry it: colour is a second channel, not the
 * only one, so the sheet survives a mono printer.
 */
const STATUS = {
  key: 'status',
  label: 'Status',
  width: '12%',
  tone: (v) => (v === 'Online' ? 'ok' : (v === 'Offline' ? 'bad' : '')),
};

export const SITE_COLUMNS = [
  SR,
  { key: 'room', label: 'Room Name', width: '17%' },
  { key: 'name', label: 'Device Name', width: '25%' },
  { key: 'type', label: 'Device Type Name', width: '21%' },
  { key: 'uno', label: 'Unique Number', width: '17%', mono: true },
  STATUS,
];

export const ESTATE_COLUMNS = [
  { ...SR, width: '7%' },
  { key: 'location', label: 'Location Name', width: '17%' },
  { key: 'room', label: 'Room Name', width: '14%' },
  { key: 'name', label: 'Device Name', width: '20%' },
  { key: 'type', label: 'Device Type Name', width: '17%' },
  { key: 'uno', label: 'Unique Number', width: '15%', mono: true },
  { ...STATUS, width: '10%' },
];

// The count tables sit two to a row, so these percentages are of HALF a page.
const COUNT_SR = { ...SR, width: '11%' };
const PIECES = { key: 'pieces', label: 'Pieces', width: '17%', align: 'right' };

export const TYPE_COLUMNS = [
  COUNT_SR, { key: 'name', label: 'Device Type Name', width: '39%' }, PIECES, ONLINE, OFFLINE,
];
export const ROOM_COLUMNS = [
  COUNT_SR, { key: 'name', label: 'Room Name', width: '39%' }, PIECES, ONLINE, OFFLINE,
];
export const LOCATION_COLUMNS = [
  COUNT_SR, { key: 'name', label: 'Location Name', width: '39%' }, PIECES, ONLINE, OFFLINE,
];

/** The flat sheet the spreadsheet gets — every column, on one filterable table. */
export const EXCEL_COLUMNS = [
  { key: 'sr', label: 'Sr. No.', width: 8, type: 'number' },
  { key: 'location', label: 'Location Name', width: 28 },
  { key: 'room', label: 'Room Name', width: 22 },
  { key: 'name', label: 'Device Name', width: 30 },
  { key: 'type', label: 'Device Type Name', width: 26 },
  { key: 'uno', label: 'Unique Number', width: 20 },
  { key: 'status', label: 'Status', width: 12 },
];

// ── Rows ───────────────────────────────────────────────────────────────────

const text = (v) => String(v ?? '').trim();

/*
 * One installed device, normalised.
 *
 * ⚠️ `status` IS THREE-VALUED and the third value is not "Offline". `liveDotOf`
 * returns null when the row carries no heartbeat column at all, and printing
 * that as Offline on an inventory would have an engineer drive out to a device
 * nobody has ever asked about. It reads "—".
 */
export const ONLINE_TEXT = 'Online';
export const OFFLINE_TEXT = 'Offline';
export const UNKNOWN_TEXT = '—';

export const installedRow = (d, locationName = '') => ({
  uno: text(d.UniqueNumber),
  name: text(d.DeviceName) || text(d.UniqueNumber) || '(unnamed)',
  type: text(d.DeviceTypeName) || '—',
  room: text(d.RoomName) || COMMON_ROOM,
  location: text(locationName),
  status: (() => {
    const live = liveDotOf(d);
    return live == null ? UNKNOWN_TEXT : (live ? ONLINE_TEXT : OFFLINE_TEXT);
  })(),
});

/** Numbered from 1, in the order given. */
const numbered = (rows) => rows.map((r, i) => ({ ...r, sr: i + 1 }));

/**
 * Count pieces by one field, largest first.
 *
 * Largest first because the question is "how many of what" — a bill of
 * materials is read from the top, and alphabetical order buries the 18 four-gang
 * switches somewhere in the middle. Ties fall back to the name so the order is
 * stable between two runs of the same report.
 */
export function countBy(rows, key) {
  const tally = new Map();
  (rows || []).forEach((r) => {
    const k = r[key] || '—';
    const t = tally.get(k) || { name: k, pieces: 0, online: 0, offline: 0 };
    t.pieces += 1;
    if (r.status === ONLINE_TEXT) t.online += 1;
    else if (r.status === OFFLINE_TEXT) t.offline += 1;
    // A third case, deliberately counted into neither: a device with no
    // heartbeat column is not offline, it is unmeasured. `pieces` is therefore
    // NOT always online + offline, and the header names the difference rather
    // than letting the two columns quietly fail to add up.
    tally.set(k, t);
  });
  return numbered(
    [...tally.values()]
      .sort((a, b) => b.pieces - a.pieces || String(a.name).localeCompare(String(b.name))),
  );
}

const stamp = (at) => new Date(at)
  .toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/*
 * The live tally for the header.
 *
 * ⚠️ THREE BUCKETS, NOT TWO, and the third is only shown when it is not empty.
 * A device with no heartbeat column is neither online nor offline; folding it
 * into "offline" would put working hardware on a service list, and dropping it
 * would leave Online + Offline quietly short of the piece count with nothing
 * saying why.
 */
const liveFigures = (rows) => {
  const online = rows.filter((r) => r.status === ONLINE_TEXT).length;
  const offline = rows.filter((r) => r.status === OFFLINE_TEXT).length;
  const unknown = rows.length - online - offline;
  return [
    { label: 'Online', value: String(online) },
    { label: 'Offline', value: String(offline) },
    ...(unknown ? [{ label: 'Not known', value: String(unknown) }] : []),
  ];
};

// ── The two reports ────────────────────────────────────────────────────────
//
// Both return the model core/admin/accessReport's builders return, so the same
// list view, the same printer and the same workbook writer render them — see
// ui/shared/ReportView and core/utils/xlsx.

/** One site: what is installed on the premises, whoever it is mapped to. */
export function siteInstalledReport({ location, devices, at = Date.now() }) {
  const rows = numbered(devices || []);
  const name = text(location && (location.LocationWithName || location.LocationName))
    || 'Location';

  return {
    title: `Installed Devices — ${name}`,
    excelColumns: EXCEL_COLUMNS,
    meta: [
      /*
       * ⚠️ THE ID, NOT THE NAME. The name is already the second half of the
       * title, and repeating it here made one figure five times longer than
       * every other: its grid track wrapped to two lines and shunted the last
       * two figures onto a second row, which is what left the header band
       * looking ragged. Every figure is now short enough to sit on one line.
       */
      { label: 'Location ID', value: location ? String(location.LocationID) : '—' },
      { label: 'Pieces installed', value: String(rows.length) },
      { label: 'Rooms', value: String(countBy(rows, 'room').length) },
      ...liveFigures(rows),
      { label: 'Generated', value: stamp(at) },
    ],
    sections: [
      // ⚠️ `compact` PUTS THESE TWO SIDE BY SIDE, on paper and on screen. A
      // three-column count table stretched across a full A4 is 90% whitespace
      // with a lonely number at the right margin — see ui/shared/reportLayout.
      {
        compact: true,
        title: 'Pieces by device type',
        subtitle: 'What is installed, and how many of each',
        columns: TYPE_COLUMNS,
        rows: countBy(rows, 'type'),
        empty: 'Nothing is installed at this location.',
      },
      {
        compact: true,
        title: 'Pieces by room',
        subtitle: 'Where they are',
        columns: ROOM_COLUMNS,
        rows: countBy(rows, 'room'),
        empty: 'Nothing is installed at this location.',
      },
      {
        kind: 'devices',
        title: 'Installed devices',
        subtitle: 'Every piece on the premises, regardless of who can reach it',
        scope: { location: name, room: '' },
        columns: SITE_COLUMNS,
        rows,
        empty: 'Nothing is installed at this location.',
      },
    ],
    total: rows.length,
  };
}

/** Every location: the company-wide inventory. */
export function estateInstalledReport({ devices, at = Date.now() }) {
  const rows = numbered(devices || []);
  const byLocation = countBy(rows, 'location');

  return {
    title: 'Installed Devices — All Locations',
    excelColumns: EXCEL_COLUMNS,
    meta: [
      { label: 'Locations', value: String(byLocation.length) },
      { label: 'Pieces installed', value: String(rows.length) },
      ...liveFigures(rows),
      { label: 'Generated', value: stamp(at) },
    ],
    sections: [
      {
        compact: true,
        title: 'Pieces by device type',
        subtitle: 'Across every location',
        columns: TYPE_COLUMNS,
        rows: countBy(rows, 'type'),
        empty: 'Nothing is installed anywhere.',
      },
      {
        compact: true,
        title: 'Pieces by location',
        subtitle: 'Largest site first',
        columns: LOCATION_COLUMNS,
        rows: byLocation,
        empty: 'No locations answered.',
      },
      {
        kind: 'devices',
        title: 'Installed devices',
        // ⚠️ ONE TABLE, NOT ONE SECTION PER SITE. 117 locations would be 117
        // headings and a PDF nobody scrolls; the Location column carries the
        // same information and the spreadsheet can group on it.
        subtitle: 'Every piece, everywhere — sorted by location and room',
        scope: { location: '', room: '' },
        columns: ESTATE_COLUMNS,
        rows,
        empty: 'Nothing is installed anywhere.',
      },
    ],
    total: rows.length,
  };
}

// ── Loading ────────────────────────────────────────────────────────────────

const byLocationThenRoom = (a, b) => String(a.location).localeCompare(String(b.location))
  || String(a.room).localeCompare(String(b.room))
  || String(a.name).localeCompare(String(b.name));

/**
 * What is installed at one site. ONE request.
 *
 * ⚠️ Sorted here, not left in reply order. This endpoint returns
 * `DevicesInstalled.SortOrder` (0 on every row) rather than the per-user
 * mapping order, so the reply order is the database's, not anybody's — see the
 * note in CLAUDE.md. An inventory wants rooms together, which is a sort this
 * screen can make honestly rather than a customer order it cannot.
 */
export async function loadSiteInstalled(location) {
  const res = await getDevicesByLocation(location && location.LocationID);
  const rows = Array.isArray(res && res.data) ? res.data : [];
  const name = text(location && (location.LocationWithName || location.LocationName));
  return rows.map((d) => installedRow(d, name)).sort(byLocationThenRoom);
}

/**
 * What is installed everywhere — one request per location, six at a time.
 *
 * Reuses `collectEstateDevices`, which already walks every location, stamps the
 * owner-qualified name on and DEDUPES by unique number: a device mapped at two
 * locations would otherwise be counted as two pieces, and the whole point of
 * this report is the count.
 */
export async function loadEstateInstalled(onProgress, shouldStop) {
  const found = await collectEstateDevices(onProgress, shouldStop);
  return found
    .map((d) => installedRow(d.raw, d.locationName))
    .sort(byLocationThenRoom);
}
